<template>
  <v-container
    class="auth"
    container--fluid
    grid-list-xl
  >
    <v-layout
      style="height:80vh;"
      justify-center
      align-center
      wrap
    >
      <v-flex
        xs12
        md5
        lg4
      >
        <center>
          <v-img
            :src="logo"
            :lazy-src="logo"
            :max-width="logoMaxWidth"
            contain
          />
        </center>
        <v-card>
          <v-card
            v-if="$route.query.s"
            :color="'warning'"
            style="border-radius:0;"
          >
            <center>
              <small>
                {{ $t("login.expired") }}
              </small>
            </center>
          </v-card>

          <v-form
            v-if="!submitted"
            @submit.prevent="Login"
          >
            <v-layout
              login
              wrap
            >
              <v-flex
                xs12
                md12
              >
                <h2>{{ $t("login.logIn") }}</h2>
              </v-flex>
              <v-flex
                v-show="!show2Fa"
                xs12
                md12
              >
                <v-text-field
                  id="email"
                  v-model.trim="form.email"
                  autofocus
                  :disabled="$route.query.email"
                  :error-messages="EmailErrors"
                  autocomplete="email"
                  name="email"
                  :label="$t('globals.email') + '*'"
                  required
                  @blur="$v.form.email.$touch()"
                />
              </v-flex>
              <v-flex
                v-show="!show2Fa"
                xs12
                md12
              >
                <v-text-field
                  id="password"
                  v-model.trim="form.password"
                  :error-messages="PasswordErrors"
                  autocomplete="current-password"
                  name="password"
                  :label="$t('globals.password') + '*'"
                  :type="showPassword ? 'text' : 'password'"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  required
                  @click:append="showPassword = !showPassword"
                  @blur="$v.form.password.$touch()"
                  @keyup.enter="Login"
                />
              </v-flex>
              <v-flex
                v-if="show2Fa"
                xs12
                code
              >
                <v-text-field
                  v-model.trim="form.twofa_verification_code"
                  v-mask="'######'"
                  :label="$t('security.googleCode')"
                  name="code"
                  :error-messages="CodeErrors"
                  @input="$v.form.twofa_verification_code.$touch()"
                  @blur="$v.form.twofa_verification_code.$touch()"
                  @keyup.native="checkCode"
                  @paste.native="checkCode"
                />
              </v-flex>
              <v-flex
                xs12
                submit
                md12
              >
                <v-btn
                  class="primary"
                  block
                  x-large
                  :disabled="loading"
                  :loading="loading"
                  @click="Login"
                >
                  {{ $t("register.logIn") }}
                </v-btn>
                <small class="forgot">
                  <router-link to="/resetpassword">{{
                    $t("login.forgot")
                  }}</router-link>
                </small>
              </v-flex>
            </v-layout>
          </v-form>
        </v-card>
        <v-flex
          xs12
          text-center
        >
          <small>
            {{ $t("login.notYet") }}
            <router-link to="/register">
              <strong>{{ $t("login.register") }}</strong>
            </router-link>
          </small>
        </v-flex>
        <v-flex
          xs12
          text-center
        >
          <small> powered by {{ $ls.get('config').name }}, App v{{ version }} </small>
        </v-flex>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { validationMixin } from "vuelidate";
import {
  required,
  email,
  minLength,
  maxLength,
  numeric,
} from "vuelidate/lib/validators";
import { mapMutations, mapState } from "vuex";
import { onLogin, onLogout } from "@/vue-apollo.js";
import { mask } from "vue-the-mask";

export default {
  name: "Login",
  directives: {
    mask,
  },
  mixins: [validationMixin],
  data() {
    return {
      logoLight: this.$ls.get('config').logo_light_theme,
      logoDark: this.$ls.get('config').logo_dark_theme,
      show2Fa: false,
      showPassword: false,
      loading: false,
      error: false,
      submitted: false,
      form: {
        email: "",
        password: "",
        twofa_verification_code: null,
      },
    }
  },
  validations() {
    return {
      form: this.rules,
    };
  },
  computed: {
    ...mapState("app", ["version"]),
    logo() {
      return this.$vuetify.theme.dark ? this.logoDark : this.logoLight
    },
    logoMaxWidth() {
      const config = this.$ls.get('config')
      if (config && config.public_config && config.public_config.app_auth_logo_max_width) {
        return config.public_config.app_auth_logo_max_width
      }
      return null
    },
    rules() {
      var form = {
        email: {
          required,
          email,
          maxLength: maxLength(255),
        },
        password: {
          required,
          maxLength: maxLength(255),
        },
        twofa_verification_code: {
          required,
          numeric,
          minLength: minLength(6),
          maxLength: maxLength(6),
        },
      };
      return form;
    },
    EmailErrors() {
      const errors = [];
      if (!this.$v.form.email.$dirty) return errors;
      !this.$v.form.email.required && errors.push(this.$t("register.reqEmail"));
      !this.$v.form.email.email && errors.push(this.$t("register.validEmail"));
      !this.$v.form.email.maxLength &&
        errors.push(this.$t("register.mustEmail"));
      return errors;
    },
    PasswordErrors() {
      const errors = [];
      if (!this.$v.form.password.$dirty) return errors;
      !this.$v.form.password.required &&
        errors.push(this.$t("register.reqPassword"));
      !this.$v.form.password.maxLength &&
        errors.push(this.$t("register.maxPassword"));
      return errors;
    },
    CodeErrors() {
      const errors = [];
      if (!this.$v.form.twofa_verification_code.$dirty) return errors;
      !this.$v.form.twofa_verification_code.required &&
        errors.push(this.$t("profile.reqField"));
      (!this.$v.form.twofa_verification_code.numeric ||
        !this.$v.form.twofa_verification_code.minLength ||
        !this.$v.form.twofa_verification_code.maxLength) &&
        errors.push(this.$t("profile.supportCodeError"));
      return errors;
    },
  },
  mounted() {
    this.Logout();
    if(this.$route.query.email) {
      this.form.email = this.$route.query.email;
    }
    setTimeout(() => {
      if(window.tidioChatApi) {
        window.tidioChatApi.display(false);
      }
    }, 200)
  },
  methods: {
    ...mapMutations("app", ["setIsLoggedIn"]),
    ...mapMutations("user", ["setUser"]),
    Logout() {
      this.$apollo
        .mutate({
          mutation: require("@/graphql/mutations/Logout.gql"),
        })
        .then((data) => {})
        .catch((error) => {});
      onLogout(this.$apollo.provider.defaultClient).then(() => {
        this.setUser(null);
        this.setIsLoggedIn(false);
        localStorage.removeItem("auth-token-expires");
        localStorage.removeItem("current-whitelabel");
        localStorage.removeItem("last-step");
      });
    },
    checkCode() {
      if (String(this.form.twofa_verification_code).length == 6) {
        this.Login();
      }
    },
    Login() {
      this.$v.form.email.$touch();
      this.$v.form.password.$touch();
      if (this.$v.form.email.$invalid || this.$v.form.password.$invalid) {
        return;
      }
      if (this.show2Fa) {
        this.$v.form.twofa_verification_code.$touch();
        if (this.$v.form.twofa_verification_code.$invalid) {
          return;
        }
      }
      let params = {
        username: this.form.email,
        password: this.form.password,
        source: this.$ls.get('config').source
      };
      if (this.show2Fa) {
        params.twofa_verification_code = this.form.twofa_verification_code;
      }
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: require("@/graphql/mutations/Login.gql"),
          variables: params,
        })
        .then((data) => {
          onLogin(
            this.$apollo.provider.defaultClient,
            data.data.login.access_token
          ).then(() => {
            this.setIsLoggedIn(true);
            localStorage.setItem(
              "auth-token-expires",
              data.data.login.expires_in
            );
            this.$router.push("/quiz");
          });
        })
        .catch((error) => {
          if (error.graphQLErrors[0].extensions.reason == "TwoFaError") {
            this.show2Fa = true;
            this.loading = false;
            setTimeout(() => {
              this.show2Fa = false;
              this.form.email = null;
              this.form.password = null;
              this.form.twofa_verification_code = null;
              this.$v.$reset();
            }, 120 * 1000);
            return;
          }
          this.errorHandler(error);
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss"></style>
